import { useMemo } from "react"
import { useQuery } from "@rocicorp/zero/react"

import type { CopilotScope } from "@productlane/lib/src/ai"
import { queries } from "@productlane/zero/queries"

import type { ContextMentionItem } from "./context-mention"
import type { ContextMentionSection } from "./context-mention-results"
import { buildContextMentionSections } from "./context-mention-results"

const SEARCH_LIMIT = 8
const ZERO_OPTIONS = { ttl: "none" } as const

const scopeThreadId = (scope: CopilotScope | null) =>
	scope?.kind === "thread" ? scope.threadId : ""
const scopeArticleId = (scope: CopilotScope | null) =>
	scope?.kind === "article" ? scope.articleId : ""
const scopeChangelogId = (scope: CopilotScope | null) =>
	scope?.kind === "changelog" ? scope.changelogId : ""

export const useContextMentionResults = (
	query: string,
	scope: CopilotScope | null,
): ContextMentionSection[] => {
	const [issues] = useQuery(
		queries.searchIssues({ query, limit: SEARCH_LIMIT }),
		ZERO_OPTIONS,
	)
	const [threads] = useQuery(
		queries.searchThreadTitles({ query, limit: SEARCH_LIMIT }),
		ZERO_OPTIONS,
	)
	const [projects] = useQuery(
		queries.searchProjects({ query, limit: SEARCH_LIMIT }),
		ZERO_OPTIONS,
	)
	const [articles] = useQuery(
		queries.searchHelpCenterArticles({ query, limit: SEARCH_LIMIT }),
		ZERO_OPTIONS,
	)
	const [contacts] = useQuery(
		queries.searchContacts({ query, limit: SEARCH_LIMIT }),
		ZERO_OPTIONS,
	)
	const [companies] = useQuery(
		queries.searchCompanies({ query, limit: SEARCH_LIMIT }),
		ZERO_OPTIONS,
	)
	const [users] = useQuery(queries.users(), ZERO_OPTIONS)
	const [changelogs] = useQuery(queries.changelogs(), ZERO_OPTIONS)

	const [pageThread] = useQuery(
		queries.threadByID(scopeThreadId(scope)),
		ZERO_OPTIONS,
	)
	const [pageArticle] = useQuery(
		queries.helpCenterArticleById(scopeArticleId(scope)),
		ZERO_OPTIONS,
	)

	const pageItem = useMemo((): ContextMentionItem | null => {
		if (pageThread) {
			return {
				type: "thread",
				id: pageThread.id,
				label: pageThread.title?.trim() || "Untitled thread",
				origin: pageThread.origin,
			}
		}
		if (pageArticle) {
			return {
				type: "article",
				id: pageArticle.id,
				label: pageArticle.title?.trim() || "Untitled article",
				icon: pageArticle.icon,
			}
		}
		const changelogId = scopeChangelogId(scope)
		const entry = changelogId
			? changelogs.find((row) => row.id === changelogId)
			: undefined
		if (entry) {
			return {
				type: "changelog",
				id: entry.id,
				label: entry.title?.trim() || "Untitled entry",
			}
		}
		return null
	}, [pageThread, pageArticle, changelogs, scope])

	return useMemo(() => {
		const itemsByType = {
			issue: issues.map((issue): ContextMentionItem => ({
				type: "issue",
				id: issue.id,
				label: issue.title,
				prefix: issue.identifier,
				color: issue.statusColor,
				status: issue.status,
			})),
			thread: threads.map((thread): ContextMentionItem => ({
				type: "thread",
				id: thread.id,
				label: thread.title?.trim() || "Untitled thread",
				origin: thread.origin,
			})),
			project: projects.map((project): ContextMentionItem => ({
				type: "project",
				id: project.id,
				label: project.name,
				color: project.color,
				icon: project.icon,
			})),
			article: articles.map((article): ContextMentionItem => ({
				type: "article",
				id: article.id,
				label: article.title?.trim() || "Untitled article",
				icon: article.icon,
			})),
			changelog: changelogs.map((entry): ContextMentionItem => ({
				type: "changelog",
				id: entry.id,
				label: entry.title?.trim() || "Untitled entry",
			})),
			contact: contacts.map((contact): ContextMentionItem => ({
				type: "contact",
				id: contact.id,
				label: contact.name?.trim() || contact.email,
				prefix: contact.name?.trim() ? contact.email : undefined,
				imageUrl: contact.imageUrl,
			})),
			company: companies.map((company): ContextMentionItem => ({
				type: "company",
				id: company.id,
				label: company.name,
				imageUrl: company.logoUrl,
			})),
			user: users.map((user): ContextMentionItem => ({
				type: "user",
				id: user.id,
				label: user.name?.trim() || user.email,
				imageUrl: user.imageUrl,
			})),
		}

		return buildContextMentionSections(itemsByType, query, { pageItem })
	}, [
		issues,
		threads,
		projects,
		articles,
		changelogs,
		contacts,
		companies,
		users,
		query,
		pageItem,
	])
}
